import {
    BehaviorSubject,
    combineLatest,
    firstValueFrom,
    merge,
    Observable,
    of,
    ReplaySubject, Subject,
    Subscription,
    switchMap,
} from 'rxjs';
import { arlTableData, formatPositionString, getARLTableData } from '../arl-calculation.helper';
import {
    calculationArlForm,
    CalculationArlFormDataService,
    calculationArlFormGroup,
} from '../calculation-arl-form-data.service';
import { distinctUntilChanged, filter, map, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';
import { number } from 'mathjs';
import {
    getAccountsReceivableLedgerById,
    getQuantityTypeDictionary,
} from '../../../../dave-data-module/State/selectors/accounting.selector';
import {
    compareArrays,
    getValueChangesObservableFromFormGroup,
    isNotNullOrUndefined,
    MathRound,
    uniqArray,
} from '../../../../helper/helper';
import { FormControl, FormGroup } from '@angular/forms';
import {
    AccountsReceivableLedgerEntity,
    AccountsReceivableLedgerTypeEnum, AccountsReceivableLedgerTypePreventGlobalPreCharge, CalcAccountsReceivableLedgerAmount,
} from 'src/app/dave-data-module/entities/accounts-receivable-ledger.entity';
import { State } from '../../../../dave-data-module/State';
import { Store } from '@ngrx/store';
import { MaterialDataService } from '../../../../dave-data-module/services/material-data.service';
import { LedgerImportARLCalculationPreChargeByType } from '../../../../dave-data-module/entities/ledger-import.entity';
import { Input } from '@angular/core';
import { ArlCalculationSurcharge } from '../arl-calculation-surcharge';
import { calculationArlTemplateForm } from '../calculation-arl-template-form-data.service';
import { arlBaseCostAllowanceFormGroup, ArlFormDataService, arlTableDataArlForm } from '../arl-form-data.service';
import { getDefaultVat } from 'src/app/dave-data-module/State/selectors/partners.selectors';
import { DaveMutationChangeAccountsReceivableLedgerArgs } from 'src/app/dave-data-module/graphql-types';
import { ARLCustomProperties } from 'src/app/dave-data-module/entities/accounts-receivable-ledger-template.entity';
import { ArlDataService } from '../../../../dave-data-module/services/arl-data.service';

/**
 * represent one position in the ARL table
 * implements all logic wich was previously in the components, so that the components can be rendered lasy
 */

export class ARLTableCalculationPosition {
    public positionPrefix: string;
    private arlId$ = new BehaviorSubject<number>(null); // todo wegoptimieren, da man die arlId nicht ändern kann und diese aich im parent form steht

    private childArlIdsFromState$ = this.arlId$.pipe(
        filter(isNotNullOrUndefined),
        switchMap((id) => this.store.select(getAccountsReceivableLedgerById({ id }))),
        map((arl) => arl?.ArlIds),
    );
    private childArlIdsFromParentForm$: Observable<number[]>
    private childArlIds$ = new BehaviorSubject<number[]>(null);
    public childArls$ = this.childArlIds$.pipe(
        filter(isNotNullOrUndefined),
        switchMap((ArlIds) => (ArlIds?.length ? combineLatest(ArlIds.map((id) => this.store.select(getAccountsReceivableLedgerById({ id })))) : of([] as AccountsReceivableLedgerEntity[]))),
        map((arls) => arls.filter(isNotNullOrUndefined)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private arlForms$: Observable<arlTableData[]> = this.childArls$.pipe(
        map((arls) => arls.map((a) => a.Id)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((ids) =>
            ids.length
                ? combineLatest(ids.map((id) => this.store.select(getAccountsReceivableLedgerById({ id })).pipe(distinctUntilChanged((a, b) => a?.UpdatedAt.getTime() === b?.UpdatedAt.getTime())))).pipe(
                    switchMap((arls) => this.preFetchMaterials$(arls).then(() => arls.map((arl) => arl && this.getARLTableData(arl)))),
                )
                : of([]),
        ),
        map((arls) => arls.filter(isNotNullOrUndefined)),
        shareReplay({ bufferSize: 1, refCount: true }),
    )
    public addedArls$ = new BehaviorSubject<Array<arlTableData>>([]);
    // public deletedArlIds: number[] = [];
    private pauseArlDataChange = false;
    public readonly arlData$: Observable<arlTableData<calculationArlForm>[]> = combineLatest([this.arlForms$, this.addedArls$]).pipe(
        filter(() => this.pauseArlDataChange === false),
        map((v) => v.flat()),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private formDirtyChange$ = new Subject<void>();
    public arlFormsDirty$ = this.arlData$.pipe(
        switchMap((data) =>
            data.length
                ? combineLatest([...data.map((d) => d.form.valueChanges.pipe(startWith(null))), ...this.surchargeHelper.zuschlaege.map((z) => z.form.valueChanges.pipe(startWith(null))), this.formDirtyChange$.pipe(startWith(null))]).pipe(
                    map(() => {
                        // console.log('invoice-editor-dirty-debug: arlFormsDirty$', data.filter((d) => d.form.dirty).map(d => Object.keys(d.form.controls).filter(c => d.form.controls[c].dirty).map(c => ({d, dirtyForm: c}))), this.zuschlaege.filter((z) => z.form.dirty))
                        return data.some((d) => d.form.dirty) || this.surchargeHelper.zuschlaege.some((z) => z.form.dirty)
                    }),
                )
                : of(false)
        ),
        // tap(arlFormsDirty$ => console.log('invoice-editor-dirty-debug: arlFormsDirty$', arlFormsDirty$))
    );
    public arlFormsInvalid$ = this.arlData$.pipe(switchMap((data) => (data.length ? combineLatest(data.map((d) => d.form.statusChanges.pipe(startWith(null)))).pipe(map(() => data.some((d) => d.form.invalid))) : of(false))));

    /**
     * EK Summe
     */
    public amountSum$: Observable<number>;
    public amountSumWithZuschl$: Observable<number>;

    private _childrenVisibleForm: FormControl<boolean> = null;
    public get childrenVisibleForm(){
        return this._childrenVisibleForm;
    };
    // private aRLCalculationPreChargeByTypes$ = new BehaviorSubject<LedgerImportARLCalculationPreChargeByType[]>([]);

    private _childrenVisibleFormSubscription: Subscription;
    private _childrenVisibleFormDisableSubscription: Subscription;
    private _calculateAmountSubscription: Subscription;

    private _childArlIdsSubscription: Subscription;

    private _positionPrefixSubscription: Subscription;

    private _updateArlOnAmountChangeSubscription: Subscription;
    private _setSurchargeFormsOnAmountChangeSubscriptions: Subscription[];

    private surchargeHelper: ARLTableCalculationPositionSurcharges;
    public readonly parentForm: arlTableDataArlForm;
    public readonly parentBaseCostAllowances$: Observable<Required<arlBaseCostAllowanceFormGroup['value']>[][]>;
    public readonly aRLCalculationPreChargeForAmountCalculation$: Observable<number>;

    get zuschlaege() {
        return this.surchargeHelper.zuschlaege;
    }
    constructor(
        private store: Store<State>,
        arlFormDataService: ArlFormDataService,
        private calcArlFormDataService: CalculationArlFormDataService,
        private arlDataService: ArlDataService,
        private materialDataService: MaterialDataService,
        public readonly arlId: number,
        private ledgerImportId: number,
        private multiplier: number,
        private onAmountCalculated: (amount: { BaseCost: number; SalaryBaseCost: number }) => void,
        private positionPrefix$: Observable<string>,
        private aRLCalculationPreChargeByTypes$: Observable<LedgerImportARLCalculationPreChargeByType[]>,
        aRLCalculationPreCharge$: Observable<number>,
        arlSurchargeByTypes$: Observable<{
            percentage: number
            arlType: AccountsReceivableLedgerTypeEnum
        }[]>,
        ) {
        this.arlId$.next(arlId);
        this.parentForm = arlFormDataService.getForm(arlId);
        // console.log(this.parentForm);
        if (!this.parentForm) {
            console.error('noParentForm');
        }/* else {
            this.setUseZSSubscription();
        }*/
        this.setChildrenVisibleForm(this.parentForm.controls.childrenVisible);
        this.parentBaseCostAllowances$ = arlFormDataService.parentBaseCostAllowances$(arlId, true);
        this.surchargeHelper = new ARLTableCalculationPositionSurcharges(
            this.store,
            this.parentForm,
            this.arlData$,
            this.arlId$.pipe(
                filter(id => !!id),
                switchMap(arlId => {
                    // console.log('### baseCostAllowances$ arlId', arlId);
                    return arlFormDataService.parentBaseCostAllowances$(arlId, true)
                }),
                // tap(bcas => console.log('### baseCostAllowances$', bcas)),
                // map(baseCostAllowances => baseCostAllowances.flat().filter().map(bca => ({arlType: bca.arlType, percentage: bca.percent})))
                shareReplay({ bufferSize: 1, refCount: true }),
                ),
            aRLCalculationPreCharge$,
            arlSurchargeByTypes$,
            );
        this.amountSum$ = combineLatest(this.surchargeHelper.zuschlaege.map((z) => [z.amountSum$, z.amountSumForIndividuallySurcharge$]).flat()).pipe(
            map((values) => {
                return values.reduce((prev, curr) => prev + (curr || 0), 0);
            }),
        );
        this.amountSumWithZuschl$ = combineLatest(this.surchargeHelper.zuschlaege.map((z) => [z.amountSumWithZuschl$, z.amountSumWithZuschlForIndividuallySurcharge$]).flat()).pipe(
            map((values) => values.reduce((prev, curr) => prev + (curr || 0), 0)),
        );
        this.aRLCalculationPreChargeForAmountCalculation$ = this.surchargeHelper.aRLCalculationPreChargeForAmountCalculation$;

        this.childArlIdsFromParentForm$ = this.parentForm.controls.ARLIds.valueChanges.pipe(
            startWith(null),
            map(() => this.parentForm.controls.ARLIds.getRawValue()),
        );
        this.init();
    }
    private init() {
        this._positionPrefixSubscription = this.positionPrefix$.subscribe((prefix) => this.setPositionPrefix(prefix));
        // this._childArlIdsSubscription = this.childArlIdsFromState$.subscribe((ids) => this.childArlIds$.next(ids)); // todo ggf direkt das observable nutzen und das behavious subject weg lassen?
        this._childArlIdsSubscription = this.childArlIdsFromParentForm$.subscribe((ids) => this.childArlIds$.next(ids)); // todo ggf direkt das observable nutzen und das behavious subject weg lassen?
        this._calculateAmountSubscription = combineLatest([this.amountSumWithZuschl$, this.parentForm.controls.InheritFromChildren ? this.parentForm.controls.InheritFromChildren.valueChanges.pipe(startWith(null)) : of(null)])
            .pipe(withLatestFrom(this.surchargeHelper.employeeZuschl.amountSumWithZuschl$, this.surchargeHelper.employeeZuschl.amountSumWithZuschlForIndividuallySurcharge$))
            .subscribe(([[amount], employeeAmount, employeeAmountIndividuals]) =>
                this.onAmountCalculated({
                    // todo ggf costAmount setzen ?
                    BaseCost: parseFloat((MathRound(amount * 100, true) / 100).toFixed(2)),
                    SalaryBaseCost: parseFloat((MathRound((employeeAmount + employeeAmountIndividuals) * 100, true) / 100).toFixed(3)),
                }),
            );
        this._childrenVisibleFormDisableSubscription = this.arlData$.subscribe((d) => {
                if (this._childrenVisibleForm) {
                    if (!d.length && this._childrenVisibleForm.enabled) {
                        this._childrenVisibleForm.disable();
                    } else if (!!d.length && this._childrenVisibleForm.disabled) {
                        this._childrenVisibleForm.enable();
                    }
                }
            });
        this._updateArlOnAmountChangeSubscription = merge(...this.surchargeHelper.zuschlaege.map((z) => z.amountSumWithZuschl$))
            .subscribe(() => {
                const form = this.parentForm;
                if (form) {
                    // console.log('update zuschl');
                    this.updateParent(form.controls.ARLIds.getRawValue(), this.arlId);
                }
            });
        this._setSurchargeFormsOnAmountChangeSubscriptions = this.surchargeHelper.zuschlaege.map((z) =>
            z.amountSum$.pipe(distinctUntilChanged()).subscribe((amountSum) => {
                const { usePercent, percent, decimal } = z.form.value;
                z.setFormValue(usePercent, amountSum, usePercent ? percent : decimal, usePercent);
            }),
        );

    }
    destroy() {
        this._childrenVisibleFormSubscription?.unsubscribe();
        this._childArlIdsSubscription?.unsubscribe();
        this._calculateAmountSubscription?.unsubscribe();
        this._childrenVisibleFormDisableSubscription?.unsubscribe();
        this._updateArlOnAmountChangeSubscription?.unsubscribe();
        this._setSurchargeFormsOnAmountChangeSubscriptions?.forEach((s) => s.unsubscribe());
        this._positionPrefixSubscription?.unsubscribe();
        this.surchargeHelper.destroy();
    }

    submit() {
        const visible = !!this._childrenVisibleForm.value;
        const childrenVisibleFormDirty = this._childrenVisibleForm.dirty;
        const zuschlDirty = this.surchargeHelper.zuschlaege.some((z) => z.form.dirty);
        return firstValueFrom(combineLatest([this.arlForms$, this.addedArls$, this.arlId$, this.store.select(getDefaultVat)])).then(([arlForms, addedArls, parentArlId, defaultVat]) => {
            const toChange: DaveMutationChangeAccountsReceivableLedgerArgs[] = [];
            if (arlForms.every((a) => a.form.valid || !a.form.dirty) && addedArls.every((a) => a.form.valid || !a.form.dirty)) {
                // console.log({ visible });
                arlForms
                    .filter((arl) => arl.form.dirty || childrenVisibleFormDirty || zuschlDirty)
                    .forEach((arl) => {
                        const cp: ARLCustomProperties = {
                            individuallyCostCalculation: arl.form.value.IndividuallyCostCalculation,
                        };
                        toChange.push({
                            itemNumber: arl.form.value.ItemNumber,
                            id: arl.arl.Id,
                            amount: CalcAccountsReceivableLedgerAmount(arl.form.value.Quantity, arl.arl.Tax, arl.getBaseCost()),
                            costAmount: arl.form.value.CostAmount || 0,
                            baseCost: arl.getBaseCost() || 0,
                            lohnBaseCostPart: arl.form.value.Type === AccountsReceivableLedgerTypeEnum.EmployeeArl ? arl.getBaseCost() : 0 || 0,
                            quantity: arl.form.controls.Quantity.getRawValue() || 0,
                            quantityTypeId: arl.form.value.QuantityTypeId,
                            information: arl.form.controls.Information.getRawValue(),
                            bookingText: arl.form.controls.BookingText.getRawValue(),
                            multiplikator: arl.arl.Multiplier,
                            isVisible: visible,
                            type: arl.form.value.Type, // todo auf type umbauen
                            // todo save zuschl
                            resourceId: arl.form.value.ResourceId,
                            jobSpecificationId: arl.form.value.JobSpecificationId,
                            materialId: arl.form.value.MaterialId,
                            isOptional: arl.form.value.IsOptional,
                            isCrossedOut: arl.form.value.CrossedOut,
                            customProperties: JSON.stringify(cp),
                            supplierId: arl.form.value.SupplierId,
                            costAmountAllowance: arl.form.value.CostAmountAllowance,
                            quantityFormula: arl.form.value.QuantityFormula,
                            surchargeOverride: isNotNullOrUndefined(arl.form.value.SurchargeOverride) ? arl.form.value.SurchargeOverride : null,
                        });
                    });

                if (addedArls?.length) {
                    return this.arlDataService
                        .addArls(
                            addedArls.map((arl) => {
                                return getCalculationArlCreatePayloadFromForm(arl.form, this.ledgerImportId, visible, parentArlId, arl.getBaseCost(), defaultVat);
                            }),
                        )
                        .then((arls) => {
                            // console.log({ arls, addedArls });
                            if (arls.length && arls.every((a) => a?.Id)) {
                                // console.log('addedArls', arls);
                                const arlIds = [...arlForms.map((a) => a.arl.Id), ...arls.map((a) => a.Id)];
                                this.pauseArlDataChange = true;
                                this.childArlIds$.next([...this.childArlIds$.value, ...arls.map((a) => a.Id)]);
                                this.addedArls$.next([]);
                                setTimeout(() => this.pauseArlDataChange = false, 1);
                                arls.slice()
                                    .reverse()
                                    .forEach((arl, index) => {
                                        // console.log(this.parentForm.controls.childArlsCalculation.length, index);
                                        this.parentForm.controls.childArlsCalculation.at(this.parentForm.controls.childArlsCalculation.length - (index + 1)).patchValue({ Id: arl.Id });
                                    });
                                // console.log(this.parentForm.controls.childArlsCalculation.value);
                                this.updateParent(arlIds, parentArlId);
                                return this.submitArls(arlIds, parentArlId, toChange, arlForms);
                                // unnötig, da die fortan nicht mehr added sind
                                // addedArls.forEach(a => a.form.markAsPristine())
                            } else {
                                throw 'arls anlegen fehlgeschlagen';
                            }
                        });
                } else {
                    const arlIds = arlForms.map((a) => a.arl.Id);
                    this.updateParent(arlIds, parentArlId);
                    return this.submitArls(arlIds, parentArlId, toChange, arlForms);
                }
            } else {
                throw 'arl form not valid';
            }
        });
    }
    private updateParent = (arlIds: number[], parentArlId: number) => {
        // console.log('updateParent');
        const parentForm = this.parentForm;
        if (!parentForm) {
            // console.error('parentForm not found', parentArlId);
        } else {
            const { ARLIds, ZuschlaegeResourcen, ZuschlaegeMaterials, ZuschlaegeDefault, ZuschlaegeEmployee, ZuschlaegeExternalService, ZuschlaegeOther } = parentForm.getRawValue();
            // console.log('updateParent', { ZuschlaegeResourcen, ZuschlaegeDefault, ZuschlaegeEmployee, ZuschlaegeExternalService, ZuschlaegeOther });
            if (!compareArrays(ARLIds, arlIds)) {
                parentForm.controls.ARLIds.setValue(arlIds);
                parentForm.controls.ARLIds.markAsDirty();
            }
            if (this.surchargeHelper.resourceZuschl.formInitialised$.value && (ZuschlaegeResourcen || 0) !== (this.surchargeHelper.resourceZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeResourcen.setValue(this.surchargeHelper.resourceZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeResourcen.markAsDirty();
            }
            if (this.surchargeHelper.materialZuschl.formInitialised$.value && (ZuschlaegeMaterials || 0) !== (this.surchargeHelper.materialZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeMaterials.setValue(this.surchargeHelper.materialZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeMaterials.markAsDirty();
            }
            if (this.surchargeHelper.defaultZuschl.formInitialised$.value && (ZuschlaegeDefault || 0) !== (this.surchargeHelper.defaultZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeDefault.setValue(this.surchargeHelper.defaultZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeDefault.markAsDirty();
            }
            if (this.surchargeHelper.employeeZuschl.formInitialised$.value && (ZuschlaegeEmployee || 0) !== (this.surchargeHelper.employeeZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeEmployee.setValue(this.surchargeHelper.employeeZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeEmployee.markAsDirty();
            }
            if (this.surchargeHelper.externalZuschl.formInitialised$.value && (ZuschlaegeExternalService || 0) !== (this.surchargeHelper.externalZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeExternalService.setValue(this.surchargeHelper.externalZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeExternalService.markAsDirty();
            }
            if (this.surchargeHelper.otherZuschl.formInitialised$.value && (ZuschlaegeOther || 0) !== (this.surchargeHelper.otherZuschl.form.value.decimal || 0)) {
                parentForm.controls.ZuschlaegeOther.setValue(this.surchargeHelper.otherZuschl.form.value.decimal);
                parentForm.controls.ZuschlaegeOther.markAsDirty();
            }
        }
        // console.log(parentForm);
        // console.log(parentForm.controls.ARLIds.value.slice());
    };
    private submitArls(arlIds: number[], parentArlId: number, toChange: DaveMutationChangeAccountsReceivableLedgerArgs[], arlForms: arlTableData[]) {
        const parentForm = this.parentForm;
        if (!parentForm) {
            toChange.push({
                id: parentArlId,
                multiplikator: this.multiplier,
                arlIds, //: [...arlForms.map((a) => a.arl.Id), ...arls.map((a) => a.Id)],
                //     // zuschläge in additionaldata speichern (wird jetzt in arl-table-component gemacht)
            });
        }
        return (
            toChange.length
                ? this.arlDataService.changeArls([
                    ...toChange,
                    // {
                    //     id: parentArlId,
                    //     multiplikator: this.multiplier,
                    //     arlIds, //: [...arlForms.map((a) => a.arl.Id), ...arls.map((a) => a.Id)],
                    //     // zuschläge in additionaldata speichern (wird jetzt in arl-table-component gemacht)
                    // },
                ])
                : firstValueFrom(of([]))
        ).then(() => {
            this.addedArls$.next([]);

            if (this._childrenVisibleForm.dirty) {
                this._childrenVisibleForm.markAsPristine();
            }
            this.surchargeHelper.zuschlaege.forEach((z) => z.form.markAsPristine());

            arlForms.forEach((d) => {
                if (d.form.dirty) {
                    d.form.markAsPristine();
                }
            });
            this.formDirtyChange$.next();
        });
    }

    reset() {
        this.addedArls$.next([]);
        firstValueFrom(this.arlForms$).then((data) => {
            data.forEach((d) => d.form.reset());
        });
        this.setParentFormArlIds();
    }
    async setParentFormArlIds() {
        const arls = await firstValueFrom(this.arlForms$);
        this.parentForm?.controls.ARLIds.setValue([...arls.map((a) => a.arl.Id), ...this.addedArls$.value.map(() => null)]);
    }
    // setARLCalculationPreChargeByTypes(v: LedgerImportARLCalculationPreChargeByType[]) {
    //     // todo in service setzen
    //     if (JSON.stringify(this.aRLCalculationPreChargeByTypes$.value) !== JSON.stringify(v)) {
    //         // console.log('set ARLCalculationPreChargeByTypes', v);
    //         this.aRLCalculationPreChargeByTypes$.next(v);
    //     }
    // }
    private async setPositionPrefix(val: string) {
        this.positionPrefix = (val && val + '') || '';
        await this.updateItemNumber();
    }
    private setChildrenVisibleForm(form: FormControl<boolean>) {
        this._childrenVisibleForm = form;
        if (form) {
            this._childrenVisibleFormSubscription?.unsubscribe();
            this._childrenVisibleFormSubscription = form.valueChanges.subscribe((visible) => {
                firstValueFrom(this.arlData$).then((data) => {
                    data.forEach((d) => {
                        if (visible !== d.form.controls.IsVisible.value) {
                            d.form.controls.IsVisible.setValue(visible);
                            d.form.controls.IsVisible.markAsDirty();
                        }
                    });
                });
            });
        }
    }
    getARLTableData(arl: AccountsReceivableLedgerEntity | null, initialValue?: Partial<calculationArlFormGroup['value']>): arlTableData<calculationArlForm> {
        if (arl && initialValue) {
            throw new Error('initialValue is not allowed if arl exists');
        }
        let form: calculationArlFormGroup = /* arl && */ this.calcArlFormDataService.getFormByEntity(arl, this._childrenVisibleForm.value);
        if (initialValue) {
            form.patchValue(initialValue);
        }
        return getARLTableData(arl, form, this.store, this.surchargeHelper.zuschlaege, (materialId) => this.materialDataService.getMaterialById$(materialId), this.aRLCalculationPreChargeByTypes$);
    }
    // mitlerweile ehr unnötig, da die durch einen guard (invoice-editor.resolver.ts) beim öffnen der rechnung prefetched werden
    private preFetchMaterials$ = async (arls: AccountsReceivableLedgerEntity[]) => {
       return firstValueFrom(of(null));
        // const materialIds = uniqArray(arls.map((arl) => arl.MaterialId).filter(isNotNullOrUndefined));
        // if (materialIds.length) {
        //     await firstValueFrom(this.materialDataService.getMaterialsById$(materialIds));
        // }
    };

    async updateItemNumber() {
        const d = await firstValueFrom(this.arlData$);
        d.forEach((data, index) => {
            const newPositionNumber = formatPositionString(this.positionPrefix + (index + 1) + '.');
            if (data.form.controls.ItemNumber.getRawValue() !== newPositionNumber) {
                data.form.controls.ItemNumber.markAsDirty();
                data.form.controls.ItemNumber.setValue(newPositionNumber);
            }
        });
    }
    setZuschlInPercent(arlType: AccountsReceivableLedgerTypeEnum, surcharge: number) {
        this.surchargeHelper.setZuschlInPercent(arlType, surcharge);
    }
    public getChildFormValues = () =>
        firstValueFrom(this.arlData$).then((data) => {
            return data.map((d) => ({ ...d.form.value, getBaseCost: d.getBaseCost }));
        });
}





export class ARLTableCalculationPositionSurcharges {
    public defaultZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;
    public resourceZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;
    public materialZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;
    public employeeZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;
    public externalZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;
    public otherZuschl:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>;

    public zuschlaege:  ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[];
    private zsOverrideSub: Subscription;
    private useZSSubscription: Subscription;
    private initZsSubscriptions: Subscription[];


    private useZS$ = new ReplaySubject<boolean>(1); // todo wegoptimieren
    public aRLCalculationPreChargeForAmountCalculation$ = combineLatest([this.aRLCalculationPreCharge$, this.useZS$]).pipe(
        map(([zs, useZs]) => useZs ? zs : 0),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    )

    constructor(
        private store: Store<State>,
        private parentForm: arlTableDataArlForm,
        private arlData$: Observable<arlTableData<calculationArlForm>[]>,
        private baseCostAllowances$: Observable<Required<arlBaseCostAllowanceFormGroup['value']>[][]>,
        private aRLCalculationPreCharge$: Observable<number>,
        private arlSurchargeByTypes$: Observable<{
            percentage: number
            arlType: AccountsReceivableLedgerTypeEnum
        }[]>,
    ) {

        this.useZSSubscription = merge(
            this.parentForm.controls.QuantityTypeId.valueChanges,
            this.parentForm.controls.Type.valueChanges
        ).pipe(
            startWith(null),
            withLatestFrom(this.store.select(getQuantityTypeDictionary)),
        ).subscribe(([_, quantityTypeDictionary]) => {
            const useZS = !AccountsReceivableLedgerTypePreventGlobalPreCharge(this.parentForm.controls.Type.getRawValue()) && !(quantityTypeDictionary[this.parentForm.controls.QuantityTypeId.getRawValue()]?.PreventGlobalPreCharge);
            // console.log('zs-update: useZS', useZS)
            this.useZS$.next(useZS);
        });




        this.defaultZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.Arl, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.Arl), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.Arl)/*.pipe(tap(v => console.log('leistungs surcharge override', v)))*/);
        this.resourceZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.ResourceArl, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.ResourceArl), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.ResourceArl));
        this.materialZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.MaterialArl, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.MaterialArl), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.MaterialArl));
        this.employeeZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.EmployeeArl, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.EmployeeArl), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.EmployeeArl));
        this.externalZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.ExternalService, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.ExternalService), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.ExternalService));
        this.otherZuschl = new ArlCalculationSurcharge(AccountsReceivableLedgerTypeEnum.Other, this.arlData$, this.aRLCalculationPreChargeForAmountCalculation$, this.getArlSurchargeByTypes$(AccountsReceivableLedgerTypeEnum.Other), this.getSurchargeOverrideByType$(AccountsReceivableLedgerTypeEnum.Other));
        this.zuschlaege = [this.defaultZuschl, this.resourceZuschl, this.materialZuschl, this.employeeZuschl, this.externalZuschl, this.otherZuschl];

        this.zsOverrideSub = merge(...this.zuschlaege.map(z => combineLatest([
            z.overrideZS$.pipe(filter(zs => isNotNullOrUndefined(zs))),
            z.formInitialised$.pipe(filter(i => i)),
        ]).pipe(map(([ov, _]) => ({calcZuschlag: z, overrideZS: ov}))))).subscribe(z => {
            z.calcZuschlag.setPercent(z.overrideZS);
        })
        this.initZsSubscriptions = [
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeResourcen').subscribe(val => this.resourceZuschl.init(val)),
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeMaterials').subscribe(val => this.materialZuschl.init(val)),
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeDefault').subscribe(val => this.defaultZuschl.init(val)),
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeEmployee').subscribe(val => this.employeeZuschl.init(val)),
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeExternalService').subscribe(val => this.externalZuschl.init(val)),
            getValueChangesObservableFromFormGroup(parentForm, 'ZuschlaegeOther').subscribe(val => this.otherZuschl.init(val)),
        ];

    }
    destroy() {
        this.zsOverrideSub.unsubscribe();
        this.useZSSubscription?.unsubscribe();
        this.initZsSubscriptions?.forEach(sub => sub.unsubscribe());
    }

    private getArlSurchargeByTypes$ (arlType: AccountsReceivableLedgerTypeEnum) {
        return this.arlSurchargeByTypes$.pipe(map(a => a.find(sur => sur.arlType === arlType)?.percentage))
    }
    private getSurchargeOverrideByType$ (arlType: AccountsReceivableLedgerTypeEnum) {
        return this.baseCostAllowances$.pipe(
            map(bcas => {
                // console.log('surcharge overrides', bcas)
                return bcas.flat().find(bca => bca.arlType === arlType && bca.type === 'surchargeOverride')?.percent
            }),
        );
    }
    setZuschlInPercent(arlType: AccountsReceivableLedgerTypeEnum, surcharge: number) {
        switch (arlType) {
            case AccountsReceivableLedgerTypeEnum.Text:
            case AccountsReceivableLedgerTypeEnum.Group:
                break;
            case AccountsReceivableLedgerTypeEnum.Arl:
                if (this.defaultZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.defaultZuschl.setPercent(surcharge)
                }
                break;
            case AccountsReceivableLedgerTypeEnum.EmployeeArl:
                if (this.employeeZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.employeeZuschl.setPercent(surcharge)
                }
                break;
            case AccountsReceivableLedgerTypeEnum.MaterialArl:
                if (this.materialZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.materialZuschl.setPercent(surcharge);
                }
                break;
            case AccountsReceivableLedgerTypeEnum.ResourceArl:
                if (this.resourceZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.resourceZuschl.setPercent(surcharge);
                }
                break;
            case AccountsReceivableLedgerTypeEnum.ExternalService:
                if (this.externalZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.externalZuschl.setPercent(surcharge);
                }
                break;
            case AccountsReceivableLedgerTypeEnum.Other:
                if (this.otherZuschl.form.controls.percent.getRawValue() !== surcharge) {
                    this.otherZuschl.setPercent(surcharge);
                }
                break;
            default:
                throw new Error(`unknown arl type ${arlType}`);
        }
    }
}

export function getCalculationArlCreatePayloadFromForm(form: FormGroup<calculationArlForm>, ledgerImportId: number, isVisible: boolean, parentArlId: number, baseCost: number, tax: number) {
    const cp: ARLCustomProperties = {
        individuallyCostCalculation: form.value.IndividuallyCostCalculation,
    };
    return {
        itemNumber: form.value.ItemNumber,
        amount: CalcAccountsReceivableLedgerAmount(form.value.Quantity || 0, tax, baseCost || 0),
        costAmount: form.value.CostAmount || 0,
        baseCost: baseCost || 0,
        lohnBaseCostPart: form.value.Type === AccountsReceivableLedgerTypeEnum.EmployeeArl ? baseCost : 0 || 0,
        quantity: form.controls.Quantity.getRawValue() || 0,
        quantityTypeId: form.value.QuantityTypeId,
        information: form.controls.Information.getRawValue(),
        bookingText: form.value.BookingText,
        parentId: parentArlId,
        ledgerImportId: ledgerImportId,
        multiplikator: 1,
        isVisible: isVisible,
        type: form.value.Type as any,
        // todo save zuschl
        resourceId: form.value.ResourceId,
        materialId: form.value.MaterialId,
        jobSpecificationId: form.value.JobSpecificationId,
        isOptional: form.value.IsOptional,
        crossedOut: form.value.CrossedOut,
        customProperties: JSON.stringify(cp),
        supplierId: form.value.SupplierId,
        costAmountAllowance: form.value.CostAmountAllowance,
        quantityFormula: form.value.QuantityFormula,
        surchargeOverride: isNotNullOrUndefined(form.value.SurchargeOverride) ? form.value.SurchargeOverride : null
    };
}
